package features.videoDetails.data

import com.kinoshazam.analytics.userIdFromJwt
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.data.TokenRepository
import data.local.DelightLocalRepository
import features.videoDetails.data.model.FavoriteEntity
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import models.FavoriteVideo

class FavoriteInteractor(
    private val delightLocalRepository: DelightLocalRepository,
    private val favoriteRepository: FavoriteRepository,
    private val globalStateRepository: GlobalStateRepository,
    private val tokenRepository: TokenRepository,
) {
    private val mutex = Mutex()
    private var syncGeneration = 0
    private val _syncing = MutableStateFlow(false)
    val syncing: StateFlow<Boolean> = _syncing.asStateFlow()

    suspend fun addFavorite(favoriteVideo: FavoriteVideo) {
        mutex.withLock { delightLocalRepository.addFavorite(favoriteVideo) }
        if (sessionUserId() == null) return
        runCatching { favoriteRepository.addFavorite(favoriteVideo) }
    }

    suspend fun deleteFavorite(video: FavoriteVideo) {
        mutex.withLock { delightLocalRepository.deleteFavorite(video) }
        if (sessionUserId() == null) return
        runCatching { favoriteRepository.deleteFavorite(video.videoId) }
    }

    suspend fun getFavoriteList(): Flow<List<FavoriteEntity>> {
        return delightLocalRepository.getAllFavoriteList(false)
    }

    /** Logout drops the device list. No server call: the account copy stays on the server. */
    suspend fun clearOnLogout() {
        mutex.withLock { delightLocalRepository.clearFavorites() }
    }

    /**
     * Uploads favorites saved on this device and replaces the local list
     * with the merged account list. Does not run when logged out.
     */
    suspend fun syncFavorites() {
        val userId = sessionUserId() ?: return
        val generation = beginSync()
        try {
            val local = mutex.withLock {
                delightLocalRepository.getAllFavoriteList(withDeleted = true).first()
            }
            val remote = runCatching { favoriteRepository.syncFavorites(local) }.getOrNull() ?: return
            mutex.withLock {
                if (sessionUserId() != userId) return
                delightLocalRepository.addAllFavorite(remote)
            }
        } finally {
            endSync(generation)
        }
    }

    suspend fun isFavorite(videoId: Int) = delightLocalRepository.isFavorite(videoId)

    private fun sessionUserId(): String? {
        if (!globalStateRepository.isAuthorized.value) return null
        return tokenRepository.getAccessToken()?.let(::userIdFromJwt)
    }

    private suspend fun beginSync(): Int = mutex.withLock {
        syncGeneration += 1
        _syncing.value = true
        syncGeneration
    }

    private suspend fun endSync(generation: Int) {
        mutex.withLock {
            if (generation == syncGeneration) _syncing.value = false
        }
    }
}
